/* global google */
import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  GoogleMap,
  Polygon,
  Marker,
  Polyline,
} from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "80vh",
  marginTop: "5px",
};

// Normalize a stored point to {lat, lng} for map rendering
const toLatLng = (point) => ({
  lat: parseFloat(point.lat),
  lng: parseFloat(point.lng ?? point.long),
});

const GoogleMapsWithEditablePolygon = ({ existingCoordinates, setLoactions }) => {
  const initialPath = existingCoordinates?.map(toLatLng) || [];

  // `path` here is only used to decide whether to render/mount the Polygon
  // and to know if a zone exists — it is NOT passed back into <Polygon> as a
  // controlled prop. The polygon's actual geometry lives inside the Google
  // Maps object itself once mounted, and we read/write it imperatively.
  const [path, setPath] = useState(initialPath);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState([]); // {lat, lng} while drawing
  const [mapInstance, setMapInstance] = useState(null);

  const polygonRef = useRef(null);
  const listenersRef = useRef([]);
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Track the path we last set imperatively so we can tell "new zone loaded
  // from outside" apart from "our own listener just updated state".
  const lastExternalPathRef = useRef(initialPath);

  const [center, setCenter] = useState(
    initialPath.length > 0 ? initialPath[0] : { lat: 30.0444, lng: 31.2357 }
  );

  // Sync when existingCoordinates changes (e.g. edit mode loads different zone data)
  useEffect(() => {
    if (existingCoordinates?.length) {
      const formatted = existingCoordinates.map(toLatLng);
      lastExternalPathRef.current = formatted;
      setPath(formatted);
      setCenter(formatted[0]);

      // If a polygon is already mounted, push the new geometry into it
      // directly instead of relying on a `path` prop diff.
      if (polygonRef.current) {
        polygonRef.current.setPath(formatted);
      }
    }
  }, [existingCoordinates]);

  // Default to user's geolocation if no coordinates
  useEffect(() => {
    if (path.length === 0 && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      });
    }
  }, []);

  // Disable double-click zoom while drawing so it doesn't zoom on polygon close
  useEffect(() => {
    if (mapInstance) {
      mapInstance.setOptions({ disableDoubleClickZoom: isDrawing });
    }
  }, [isDrawing, mapInstance]);

  // ── Polygon edit listeners ──────────────────────────────────────────────────

  const syncPathFromPolygon = useCallback(() => {
    if (!polygonRef.current) return;
    const updated = polygonRef.current
      .getPath()
      .getArray()
      .map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
    // This is a one-way sync OUT to React/API state. We deliberately do not
    // feed `updated` back into <Polygon path={...}> — doing so is what
    // caused the wrapper to call polygon.setPath() with a new MVCArray and
    // silently detach these very listeners after the first edit.
    setPath(updated);
    setLoactions(updated.map((p) => ({ lat: p.lat, long: p.lng })));
  }, [setLoactions]);

  const attachPolygonListeners = useCallback(
    (polygon) => {
      listenersRef.current.forEach((l) => google.maps.event.removeListener(l));
      listenersRef.current = [
        google.maps.event.addListener(polygon.getPath(), "set_at", syncPathFromPolygon),
        google.maps.event.addListener(polygon.getPath(), "insert_at", syncPathFromPolygon),
        google.maps.event.addListener(polygon.getPath(), "remove_at", syncPathFromPolygon),
      ];
    },
    [syncPathFromPolygon]
  );

  const onLoadPolygon = useCallback(
    (polygon) => {
      polygonRef.current = polygon;
      // Set the initial geometry once, imperatively, at mount time.
      if (lastExternalPathRef.current.length) {
        polygon.setPath(lastExternalPathRef.current);
      }
      attachPolygonListeners(polygon);
    },
    [attachPolygonListeners]
  );

  const onUnmountPolygon = useCallback(() => {
    listenersRef.current.forEach((l) => google.maps.event.removeListener(l));
    listenersRef.current = [];
    polygonRef.current = null;
  }, []);

  // ── Drawing controls ────────────────────────────────────────────────────────

  const startDrawing = () => {
    // Remove existing polygon if present
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    lastExternalPathRef.current = [];
    setPath([]);
    setLoactions([]);
    setDrawingPoints([]);
    setIsDrawing(true);
    mapInstance?.setOptions({ draggableCursor: "crosshair" });
  };

  const cancelDrawing = () => {
    setDrawingPoints([]);
    setIsDrawing(false);
    mapInstance?.setOptions({ draggableCursor: "" });
  };

  const finishDrawing = useCallback(() => {
    if (drawingPoints.length < 3) return;
    lastExternalPathRef.current = drawingPoints;
    setPath(drawingPoints);
    setLoactions(drawingPoints.map((p) => ({ lat: p.lat, long: p.lng })));
    setDrawingPoints([]);
    setIsDrawing(false);
    mapInstance?.setOptions({ draggableCursor: "" });
  }, [drawingPoints, mapInstance, setLoactions]);

  const deletePolygon = () => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    lastExternalPathRef.current = [];
    setPath([]);
    setLoactions([]);
    setDrawingPoints([]);
    setIsDrawing(false);
    mapInstance?.setOptions({ draggableCursor: "" });
  };

  // ── Map event handlers ──────────────────────────────────────────────────────

  const handleMapClick = useCallback(
    (e) => {
      if (!isDrawing) return;
      setDrawingPoints((prev) => [
        ...prev,
        { lat: e.latLng.lat(), lng: e.latLng.lng() },
      ]);
    },
    [isDrawing]
  );

  const handleMapDblClick = useCallback(
    (e) => {
      if (!isDrawing) return;
      // dblclick fires a preceding click too — remove that extra point
      setDrawingPoints((prev) => {
        const trimmed = prev.slice(0, -1); // undo the click that fired before dblclick
        if (trimmed.length >= 3) {
          // Schedule finish after state settles
          setTimeout(() => {
            lastExternalPathRef.current = trimmed;
            setPath(trimmed);
            setLoactions(trimmed.map((p) => ({ lat: p.lat, long: p.lng })));
            setDrawingPoints([]);
            setIsDrawing(false);
            mapInstance?.setOptions({ draggableCursor: "" });
          }, 0);
          return [];
        }
        return trimmed;
      });
    },
    [isDrawing, mapInstance, setLoactions]
  );

  // ── Autocomplete ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (mapInstance && searchInputRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        { fields: ["geometry", "name"] }
      );
      autocompleteRef.current.bindTo("bounds", mapInstance);
      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (!place.geometry?.location) {
          alert(`No details available for: "${place.name}"`);
          return;
        }
        if (place.geometry.viewport) {
          mapInstance.fitBounds(place.geometry.viewport);
        } else {
          mapInstance.setCenter(place.geometry.location);
          mapInstance.setZoom(16);
        }
      });
    }
  }, [mapInstance]);

  // ── Custom map controls ─────────────────────────────────────────────────────

  const addLocationControl = (map) => {
    const btn = document.createElement("button");
    btn.textContent = "📍 My Location";
    btn.classList.add("custom-map-control-button");
    Object.assign(btn.style, {
      background: "#fff",
      border: "none",
      padding: "8px",
      margin: "10px",
      cursor: "pointer",
      borderRadius: "4px",
      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
      fontSize: "16px",
    });
    btn.addEventListener("click", () => {
      if (!navigator.geolocation) {
        alert("Your browser doesn't support geolocation.");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          map.panTo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          map.setZoom(16);
        },
        () => alert("Error: Geolocation service failed or is unavailable.")
      );
    });
    map.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(btn);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        .custom-map-control-button:hover { background: #ebebeb !important; }
        .gmap-search-box {
          position: absolute;
          top: 10px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1000;
          width: 280px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          border-radius: 4px;
          overflow: hidden;
        }
        .gmap-search-box input {
          width: 100%;
          padding: 8px 12px;
          font-size: 15px;
          border: 1px solid #ccc;
          outline: none;
          box-sizing: border-box;
        }
        .gmap-toolbar {
          display: flex;
          gap: 6px;
          padding: 6px 8px;
          background: rgba(255,255,255,0.95);
          border-bottom: 1px solid #ddd;
          flex-wrap: wrap;
        }
        .gmap-toolbar button {
          padding: 6px 12px;
          border: 1px solid #ccc;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
          background: #fff;
          transition: background 0.15s;
        }
        .gmap-toolbar button:hover { background: #f0f0f0; }
        .gmap-toolbar button.active {
          background: #e8f0fe;
          border-color: #4285f4;
          color: #1a73e8;
          font-weight: 600;
        }
        .gmap-toolbar button.danger { color: #d32f2f; border-color: #d32f2f; }
        .gmap-toolbar button.danger:hover { background: #fdecea; }
        .drawing-hint {
          position: absolute;
          bottom: 28px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0,0,0,0.72);
          color: #fff;
          padding: 7px 16px;
          border-radius: 20px;
          z-index: 1000;
          font-size: 13px;
          pointer-events: none;
          white-space: nowrap;
        }
      `}</style>

      <div style={{ position: "relative" }}>
        {/* ── Toolbar ── */}
        <div className="gmap-toolbar">
          {!isDrawing ? (
            <button onClick={startDrawing}>✏️ Draw Polygon</button>
          ) : (
            <>
              <button
                className="active"
                onClick={finishDrawing}
                disabled={drawingPoints.length < 3}
              >
                ✔ Finish ({drawingPoints.length} pts)
              </button>
              <button onClick={cancelDrawing}>✕ Cancel</button>
            </>
          )}
          {path.length > 0 && !isDrawing && (
            <button className="danger" onClick={deletePolygon}>
              🗑 Delete Polygon
            </button>
          )}
        </div>

        {/* ── Search box ── */}
        <div className="gmap-search-box" style={{ top: "58px" }}>
          <input type="text" placeholder="Search places…" ref={searchInputRef} />
        </div>

        {/* ── Drawing hints ── */}
        {isDrawing && (
          <div className="drawing-hint">
            {drawingPoints.length < 3
              ? `Click to add points (${drawingPoints.length} of 3 minimum)`
              : "Double-click or press Finish to complete the polygon"}
          </div>
        )}

        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          mapTypeId="hybrid"
          zoom={21}
          options={{
            maxZoom: 21,
            minZoom: 5,
            streetViewControl: true,
            mapTypeControl: true,
            rotateControl: true,
            gestureHandling: "greedy",
          }}
          onClick={handleMapClick}
          onDblClick={handleMapDblClick}
          onLoad={(map) => {
            setMapInstance(map);
            addLocationControl(map);
            if (initialPath.length) {
              const bounds = new window.google.maps.LatLngBounds();
              initialPath.forEach((c) => bounds.extend(c));
              map.fitBounds(bounds);
            }
          }}
        >
          {/* Completed / editable polygon — mounted based on `path.length`,
              but geometry itself is set imperatively in onLoadPolygon /
              the existingCoordinates effect, never via a `path` prop. */}
          {path.length > 0 && !isDrawing && (
            <Polygon
              options={{
                fillColor: "#0000aa",
                fillOpacity: 0.4,
                strokeColor: "#0000aa",
                strokeOpacity: 1,
                strokeWeight: 2,
                editable: true,
                draggable: false,
              }}
              onLoad={onLoadPolygon}
              onUnmount={onUnmountPolygon}
            />
          )}

          {/* In-progress drawing: vertex markers + connecting line */}
          {isDrawing && drawingPoints.length > 0 && (
            <>
              <Polyline
                path={drawingPoints}
                options={{
                  strokeColor: "#0000aa",
                  strokeOpacity: 0.9,
                  strokeWeight: 2,
                }}
              />
              {drawingPoints.map((pt, i) => (
                <Marker
                  key={i}
                  position={pt}
                  options={{
                    icon: {
                      path: google.maps.SymbolPath.CIRCLE,
                      scale: i === 0 ? 8 : 5,
                      fillColor: i === 0 ? "#ff5722" : "#0000aa",
                      fillOpacity: 1,
                      strokeColor: "#fff",
                      strokeWeight: 2,
                    },
                    title: i === 0 ? "Start point" : `Point ${i + 1}`,
                  }}
                />
              ))}
            </>
          )}
        </GoogleMap>
      </div>
    </>
  );
};

export default GoogleMapsWithEditablePolygon;